<template>
  <div class="lf-modal" role="dialog" aria-modal="true" aria-labelledby="lf-modal-title" @click.self="close">
    <div class="lf-modal__panel">

      <!-- Header -->
      <header class="lf-modal__head">
        <h2 id="lf-modal-title">Configuración de cookies</h2>
        <button class="lf-modal__close" aria-label="Cerrar" @click="close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </header>

      <!-- Intro -->
      <p class="lf-modal__intro">
        Selecciona qué categorías de cookies quieres activar. Puedes cambiar estas
        preferencias cuando quieras desde nuestra
        <router-link to="/politica-cookies" @click="close">Política de Cookies</router-link>.
      </p>

      <!-- Categorías -->
      <ul class="lf-modal__cats">
        <li v-for="cat in categories" :key="cat.key" class="lf-modal__cat">
          <div class="lf-modal__cat-info">
            <h3>{{ cat.title }}</h3>
            <p>{{ cat.description }}</p>
          </div>

          <div class="lf-modal__cat-toggle">
            <button
              type="button"
              role="switch"
              :aria-checked="cat.key === 'essential' ? true : local[cat.key]"
              :disabled="cat.key === 'essential'"
              class="lf-switch"
              :class="{ 'lf-switch--on': cat.key === 'essential' ? true : local[cat.key] }"
              @click="toggle(cat.key)"
            >
              <span class="lf-switch__dot"></span>
            </button>
          </div>
        </li>
      </ul>

      <!-- Acciones -->
      <footer class="lf-modal__actions">
        <button class="lf-btn lf-btn--ghost" @click="selectEssential">Solo esenciales</button>
        <button class="lf-btn lf-btn--ghost" @click="selectAll">Aceptar todas</button>
        <button class="lf-btn lf-btn--primary" @click="save">Guardar preferencias</button>
      </footer>

    </div>
  </div>
</template>

<script>
export default {
  name: 'CookieSettingsModal',
  props: {
    preferences: { type: Object, required: true }
  },
  emits: ['close', 'save'],
  data() {
    return {
      local: { ...this.preferences },
      categories: [
        {
          key: 'essential',
          title: 'Esenciales',
          description: 'Necesarias para el funcionamiento básico del sitio. No se pueden desactivar.'
        },
        {
          key: 'analytics',
          title: 'Analíticas',
          description: 'Nos ayudan a entender cómo se usa el sitio para mejorarlo.'
        },
        {
          key: 'functional',
          title: 'Funcionalidad',
          description: 'Recuerdan tus preferencias (idioma, región, tema visual).'
        },
        {
          key: 'marketing',
          title: 'Marketing',
          description: 'Permiten mostrar anuncios relevantes y medir campañas.'
        }
      ]
    }
  },
  watch: {
    preferences: {
      handler(v) { this.local = { ...v } },
      deep: true
    }
  },
  methods: {
    toggle(key) {
      if (key === 'essential') return
      this.local[key] = !this.local[key]
    },
    selectAll() {
      this.local = { essential: true, analytics: true, functional: true, marketing: true }
    },
    selectEssential() {
      this.local = { essential: true, analytics: false, functional: false, marketing: false }
    },
    save() {
      this.$emit('save', { ...this.local })
    },
    close() {
      this.$emit('close')
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKey)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKey)
  },
  created() {
    // método del listener declarado aquí para tener acceso a `this`
    this.onKey = (e) => { if (e.key === 'Escape') this.close() }
  }
}
</script>

<style scoped>
.lf-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1050;
  animation: lf-fade 0.25s ease;
}

.lf-modal__panel {
  background: var(--lf-surface);
  border: 1px solid var(--lf-border-2);
  border-radius: 18px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  animation: lf-scale 0.25s ease;
}

@keyframes lf-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes lf-scale {
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
}

/* Header */
.lf-modal__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.lf-modal__head h2 {
  font-size: 1.4rem;
  margin: 0;
}
.lf-modal__close {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: transparent;
  border: 1px solid var(--lf-border);
  color: var(--lf-text-mute);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s, border-color 0.2s;
}
.lf-modal__close:hover {
  color: var(--lf-text);
  border-color: var(--lf-text-mute);
}

.lf-modal__intro {
  font-size: 14px;
  color: var(--lf-text-mute);
  margin: 0 0 24px;
  line-height: 1.6;
}
.lf-modal__intro a {
  color: var(--lf-accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* Categorías */
.lf-modal__cats {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.lf-modal__cat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 18px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--lf-border);
  border-radius: 12px;
  transition: border-color 0.2s;
}
.lf-modal__cat:hover { border-color: var(--lf-border-2); }

.lf-modal__cat-info h3 {
  font-size: 0.95rem;
  margin: 0 0 4px;
}
.lf-modal__cat-info p {
  margin: 0;
  font-size: 13px;
  color: var(--lf-text-mute);
  line-height: 1.5;
}

/* Toggle */
.lf-switch {
  position: relative;
  width: 48px;
  height: 26px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--lf-border-2);
  cursor: pointer;
  padding: 0;
  transition: background 0.25s, border-color 0.25s;
  flex-shrink: 0;
}
.lf-switch:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
.lf-switch__dot {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--lf-text);
  transition: transform 0.25s ease, background 0.25s;
}
.lf-switch--on {
  background: var(--lf-accent);
  border-color: var(--lf-accent);
}
.lf-switch--on .lf-switch__dot {
  transform: translateX(22px);
  background: #001014;
}

/* Acciones */
.lf-modal__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid var(--lf-border);
}
.lf-modal__actions .lf-btn { padding: 11px 20px; font-size: 14px; }

/* Responsive */
@media (max-width: 640px) {
  .lf-modal__panel { padding: 24px 20px; }
  .lf-modal__cat {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
  .lf-modal__actions {
    flex-direction: column-reverse;
    justify-content: stretch;
  }
  .lf-modal__actions .lf-btn { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .lf-modal,
  .lf-modal__panel { animation: none; }
  .lf-switch,
  .lf-switch__dot { transition: none; }
}
</style>